// MarketplaceCategoriesWidget — quick-filter deep-link tiles + sponsored slots on Home
// Merges 8 default tiles with admin-created custom categories (icon + tint from admin).
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Star, MessageCircle } from 'lucide-react';
import api from '../utils/api';

const DEFAULTS = [
  { key: 'Electronics', label: 'Electronics', icon: '📱', tint: 'from-cyan-500 to-blue-600',        desc: 'Phones · TV · Laptops · Gadgets' },
  { key: 'Fashion',     label: 'Fashion',     icon: '👗', tint: 'from-pink-500 to-rose-600',         desc: 'Sarees · Kurtis · Menswear · Footwear' },
  { key: 'Grocery',     label: 'Grocery',     icon: '🛒', tint: 'from-emerald-500 to-teal-600',      desc: 'Dal · Rice · Oil · Daily needs' },
  { key: 'Vehicle',     label: 'Vehicle',     icon: '🚘', tint: 'from-orange-500 to-red-600',        desc: 'Cars · Bikes · Auto · Showrooms' },
  { key: 'Services',    label: 'Services',    icon: '🔧', tint: 'from-purple-500 to-fuchsia-600',    desc: 'Repair · Salon · Tuition · Book slots', kind: 'service' },
  { key: 'Home',        label: 'Home Decor',  icon: '🛋️', tint: 'from-amber-500 to-yellow-600',       desc: 'Furniture · Curtains · Lighting' },
  { key: 'Food',        label: 'Food',        icon: '🍱', tint: 'from-red-500 to-rose-600',          desc: 'Restaurants · Tiffin · Sweets · Bakery' },
  { key: 'Property',    label: 'Property',    icon: '🏢', tint: 'from-slate-500 to-slate-700',       desc: 'Rent · Sale · Plots · PG' },
];

const TINT_CYCLE = [
  'from-indigo-500 to-purple-600',
  'from-emerald-500 to-cyan-600',
  'from-orange-500 to-yellow-600',
  'from-pink-500 to-rose-600',
  'from-sky-500 to-blue-600',
];

const abs = (u) => (!u ? '' : u.startsWith('http') ? u : `${process.env.REACT_APP_BACKEND_URL || ''}${u}`);

const MarketplaceCategoriesWidget = () => {
  const [sponsored, setSponsored] = useState([]);
  const [adminCats, setAdminCats] = useState([]);

  useEffect(() => {
    api.get('/marketplace/feed', { params: { limit: 30 } })
      .then((r) => {
        const rows = r.data?.listings || [];
        const s = rows.filter((l) => l.is_sponsor || l.featured).slice(0, 2);
        setSponsored(s.length ? s : rows.slice(0, 2));
      })
      .catch(() => {});
    api.get('/marketplace/categories')
      .then((r) => setAdminCats(r.data?.categories || []))
      .catch(() => {});
  }, []);

  // Merge: defaults first, then admin (dedupe by lowercased name)
  const defaultKeys = new Set(DEFAULTS.map((d) => d.key.toLowerCase()));
  const extra = adminCats
    .filter((c) => c && c.name && !defaultKeys.has(c.name.toLowerCase()))
    .map((c, i) => ({
      key: c.name,
      label: c.name,
      icon: c.icon || '🏷️',
      tint: c.tint || TINT_CYCLE[i % TINT_CYCLE.length],
      desc: c.parent ? `Under ${c.parent}` : 'Custom category',
    }));
  const tiles = [...DEFAULTS, ...extra];

  return (
    <section className="px-4 pt-3" data-testid="marketplace-categories-widget">
      <div className="max-w-7xl mx-auto space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-xs sm:text-sm font-black uppercase tracking-widest opacity-80">
            🛍️ Marketplace · Shop by Category {extra.length > 0 && <span className="text-purple-300">· +{extra.length} new</span>}
          </div>
          <Link to="/marketplace" className="text-[10px] sm:text-xs text-purple-300 font-black flex items-center gap-0.5" data-testid="mkt-cat-view-all">
            View all <ChevronRight size={12} />
          </Link>
        </div>
        <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
          {tiles.map((c) => {
            const params = new URLSearchParams({ category: c.key });
            if (c.kind) params.set('kind', c.kind);
            return (
              <Link
                key={c.key}
                to={`/marketplace?${params.toString()}`}
                data-testid={`mkt-cat-${String(c.key).toLowerCase().replace(/\W+/g, '-')}`}
                className={`group relative rounded-2xl bg-gradient-to-br ${c.tint} p-2 sm:p-3 text-white shadow-lg hover:scale-105 transition-transform border border-white/20`}
                title={c.desc}
              >
                <div className="text-3xl sm:text-4xl leading-none">{c.icon}</div>
                <div className="text-[10px] sm:text-xs font-black mt-1 truncate">{c.label}</div>
                <div className="hidden sm:block text-[9px] opacity-80 truncate">{c.desc}</div>
              </Link>
            );
          })}
        </div>

        {/* Sponsored slots — 1-2 listings auto-injected */}
        {sponsored.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="mkt-sponsored-slots">
            {sponsored.map((l) => (
              <Link key={l.id} to={`/marketplace/store/${l.shop_slug}#listing-${l.id}`}
                    data-testid={`mkt-sponsored-${l.id}`}
                    className="group relative rounded-2xl border-2 border-yellow-400/60 bg-gradient-to-r from-slate-900 to-purple-900/60 p-3 flex items-center gap-3 hover:border-yellow-300 transition">
                {l.images?.[0] ? <img src={abs(l.images[0])} alt="" className="w-16 h-16 object-cover rounded-xl border border-white/20" /> : <div className="w-16 h-16 rounded-xl bg-slate-800 flex items-center justify-center text-3xl">📦</div>}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1 mb-0.5">
                    <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-yellow-400 text-slate-900 font-black flex items-center gap-0.5"><Star size={9} /> {l.is_sponsor ? 'SPONSORED' : 'FEATURED'}</span>
                    {l.verified && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500 text-white font-black">✓ Verified</span>}
                  </div>
                  <div className="text-sm font-black text-white truncate">{l.title}</div>
                  <div className="text-[10px] opacity-80 truncate">🏪 {l.shop_name}{l.city ? ` · 📍 ${l.city}` : ''}</div>
                  <div className="text-sm font-black text-yellow-300 mt-0.5">{l.price_at} AT</div>
                </div>
                <div className="hidden sm:flex flex-col items-end gap-1">
                  <MessageCircle size={16} className="text-green-400 group-hover:scale-110 transition" />
                  <span className="text-[9px] opacity-70">View →</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default MarketplaceCategoriesWidget;
